import {FormField, useFormContext, type FormControl} from 'platform/components';
import {Grid, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useWatch, type FieldPath} from 'react-hook-form';

import {t} from './catalogueUi';
import {NewModelCollectionSteps} from './NewModelCollectionSteps';
import type {NewModelDraft, YearDateField} from './newModelDraft';
import {NewModelEquipmentPackageSteps} from './NewModelEquipmentPackageSteps';
import {
  NewModelIdentitySteps,
  type CatalogueChoice,
  type ModelChoice,
  type NewModelFormValues,
} from './NewModelIdentitySteps';

export type {CatalogueChoice, ModelChoice, NewModelFormValues};

export function NewModelSteps(props: {
  step: number;
  isLocked: boolean;
  control: FormControl<NewModelFormValues>;
  canViewCost: boolean;
  makes: CatalogueChoice[];
  models: ModelChoice[];
  currency: string;
  goTo: (step: number) => void;
  onInvalidDateInput: (field: YearDateField, isInvalid: boolean) => void;
}) {
  const form = useFormContext<NewModelFormValues>();
  const draft = useWatch({control: props.control, name: 'draft'});
  const update = (next: NewModelDraft) => {
    form.setValue('draft', next, {shouldDirty: true});
    props.control.onChange();
  };
  const text = (
    name: FieldPath<NewModelFormValues>,
    key: string,
    label: string,
    required = false,
    placeholder?: string
  ) => (
    <FormField
      control={props.control}
      name={name}
      isDisabled={props.isLocked}
      type="text"
      label={t(key, label)}
      placeholder={placeholder}
      isRequired={required}
      onChange={(value) => {
        if (value === null) {
          form.setValue(name, '', {shouldDirty: true});
          props.control.onChange();
        }
      }}
    />
  );
  const content = match(props.step)
    .with(Pattern.union(1, 2), (step) => (
      <NewModelIdentitySteps
        key={step}
        step={step}
        draft={draft}
        isLocked={props.isLocked}
        control={props.control}
        makes={props.makes}
        models={props.models}
        update={update}
        text={text}
      />
    ))
    .with(3, (step) => (
      <VStack key={step} spacing={5}>
        <Grid columns={[1, 1, 2]} spacing={4}>
          {text(
            'draft.year.code',
            'entity.newVehicle.authoring.modelYearCode',
            'Model year code',
            true,
            t('entity.newVehicle.authoring.modelYearCodePlaceholder', 'e.g. MY2027.50')
          )}
          {text(
            'draft.year.catalogueName',
            'entity.newVehicle.catalogue.catalogueName',
            'Catalogue name',
            true,
            t('entity.newVehicle.authoring.catalogueNamePlaceholder', 'e.g. Puma retail 2027')
          )}
          {text(
            'draft.year.pricelistVersion',
            'entity.newVehicle.catalogue.pricelistVersionLabel',
            'Price list version',
            true,
            t('entity.newVehicle.authoring.priceListVersionPlaceholder', 'e.g. 2027/1')
          )}
          <VStack spacing={1}>
            <Text size="small" color="secondary">
              {t('entity.newVehicle.catalogue.currency', 'Currency')}
            </Text>
            <Text>{props.currency}</Text>
          </VStack>
          <FormField
            control={props.control}
            name="draft.year.validFrom"
            isDisabled={props.isLocked}
            type="apiDate"
            label={t('entity.newVehicle.authoring.validFrom', 'Valid from')}
            isRequired
            onInvalidInputChange={(isInvalid) => props.onInvalidDateInput('validFrom', isInvalid)}
          />
          <FormField
            control={props.control}
            name="draft.year.validTo"
            isDisabled={props.isLocked}
            type="apiDate"
            label={t('entity.newVehicle.authoring.validTo', 'Valid to')}
            onInvalidInputChange={(isInvalid) => props.onInvalidDateInput('validTo', isInvalid)}
          />
        </Grid>
      </VStack>
    ))
    .with(Pattern.union(4, 5, 6, 7), (step) => (
      <NewModelCollectionSteps
        key={step}
        step={step}
        draft={draft}
        canViewCost={props.canViewCost}
        currency={props.currency}
        isLocked={props.isLocked}
        update={update}
      />
    ))
    .with(Pattern.union(8, 9), (step) => (
      <NewModelEquipmentPackageSteps
        key={step}
        step={step}
        draft={draft}
        canViewCost={props.canViewCost}
        currency={props.currency}
        isLocked={props.isLocked}
        goTo={props.goTo}
        update={update}
      />
    ))
    .otherwise(() => null);
  return content;
}
